import React from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Typography,
} from "@mui/material";
import {
    PaymentsRounded,
} from "@mui/icons-material";

const acknowledgementPageStyle = `
  @page {
    size: 50mm 75mm;
    margin: 0;
  }

  * {
    box-sizing: border-box;
  }

  body {
    font-family: "Courier Prime", "Courier New", monospace;
    margin: 0;
    padding: 0;
    text-transform: uppercase;
  }

  .slip {
    width: 50mm;
    min-height: 75mm;
    padding: 8px 6px;
    text-align: center;
  }

  .brand {
    font-size: 11px;
    font-weight: 800;
    margin: 0 0 6px 0;
    letter-spacing: 0.4px;
  }

  .title {
    font-size: 10px;
    font-weight: 800;
    margin: 0 0 10px 0;
    padding-bottom: 6px;
    border-bottom: 1px dashed #000;
  }

  .label {
    font-size: 9px;
    font-weight: 700;
    margin: 8px 0 0 0;
  }

  .order-no {
    font-size: 22px;
    font-weight: 800;
    margin: 2px 0 0 0;
    line-height: 1.1;
  }

  .amount {
    font-size: 20px;
    font-weight: 800;
    margin: 2px 0 10px 0;
    line-height: 1.1;
  }

  .meta {
    font-size: 9px;
    font-weight: 700;
    margin: 10px 0 0 0;
    padding-top: 6px;
    border-top: 1px dashed #000;
  }

  .thanks {
    font-size: 9px;
    font-weight: 800;
    margin: 8px 0 0 0;
  }
`;

const escapeHtml = (value = "") =>
    String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

export const getOrderDisplayNumber = (createdOrder) => {
    const order = createdOrder?.order || createdOrder || {};
    const explicitNumber =
        order.orderNumber ||
        createdOrder?.orderNumber;

    if (explicitNumber) {
        const value = String(explicitNumber).trim();
        return value.startsWith("#") ? value : `#${value}`;
    }

    const sequence = order.order_sequence ?? "";
    const orderNo = order.order_no ?? "";

    if (sequence || orderNo) {
        return `#${sequence}${orderNo}`;
    }

    return "";
};

export const printAcknowledgementInvoice = ({
    orderNumber,
    amount = 0,
    outletName = "Xpanse",
} = {}) => {
    const formattedAmount = Number(amount || 0).toFixed(2);
    const displayOrderNumber = escapeHtml(orderNumber || "-");
    const displayOutletName = escapeHtml(outletName || "Xpanse");
    const printedAt = new Date();
    const date = printedAt.toLocaleDateString("en-GB");
    const time = printedAt.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const printDocument = iframe.contentWindow || iframe.contentDocument;
    const doc = printDocument.document || printDocument;

    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Payment Acknowledgement</title>
          <style>${acknowledgementPageStyle}</style>
        </head>
        <body>
          <div class="slip">
            <p class="brand">${displayOutletName}</p>
            <p class="title">Payment Acknowledgement</p>
            <p class="label">Order No</p>
            <p class="order-no">${displayOrderNumber}</p>
            <p class="label">Amount</p>
            <p class="amount">Rs. ${formattedAmount}</p>
            <p class="meta">Paid<br>${date} ${time}</p>
            <p class="thanks">Thank you</p>
          </div>
        </body>
      </html>
    `);
    doc.close();

    const cleanup = () => {
        if (iframe.parentNode) {
            document.body.removeChild(iframe);
        }
    };

    let hasPrinted = false;
    const triggerPrint = () => {
        if (hasPrinted) {
            return;
        }

        hasPrinted = true;
        iframe.focus();
        iframe.contentWindow.print();
        setTimeout(cleanup, 1500);
    };

    iframe.onload = triggerPrint;

    if (iframe.contentWindow.document.readyState === "complete") {
        triggerPrint();
    }
};

const PaymentModal = ({
    open,
    amount = 0,
    isLoading,
    onClose,
    onConfirm,
}) => {
    const formattedAmount = Number(amount || 0).toFixed(2);
    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    mx: 2,
                    maxWidth: "420px",
                    borderRadius: "24px",
                    overflow: "hidden",
                    boxShadow:
                        "0 25px 70px rgba(19, 76, 110, 0.3)",
                },
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: "rgba(12, 42, 58, 0.7)",
                    backdropFilter: "blur(5px)",
                },
            }}
        >
            <DialogContent
                sx={{
                    p: {
                        xs: 2.5,
                        sm: 3,
                    },
                    textAlign: "center",
                    background:
                        "linear-gradient(160deg, #ffffff 0%, #edf8fd 100%)",
                }}
            >
                <Box
                    sx={{
                        width: "72px",
                        height: "72px",
                        mx: "auto",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "22px",
                        color: "#ffffff",
                        background:
                            "linear-gradient(145deg, #25ba6c, #1d9e59)",
                        boxShadow:
                            "0 14px 30px rgba(37, 186, 108, 0.25)",
                    }}
                >
                    <PaymentsRounded
                        sx={{
                            fontSize: "40px",
                        }}
                    />
                </Box>

                <Typography
                    component="h2"
                    sx={{
                        color: "#17364a",
                        fontSize: {
                            xs: "22px",
                            sm: "25px",
                        },
                        fontWeight: 800,
                        lineHeight: 1.2,
                        mb: 1,
                    }}
                >
                    Payment received? ₹{formattedAmount}
                </Typography>

                <Typography
                    sx={{
                        color: "#577383",
                        fontSize: "15px",
                        lineHeight: 1.6,
                        px: 1,
                    }}
                >
                    Please confirm that the customer&apos;s payment
                    has been successfully received on the Pine Labs
                    terminal.
                </Typography>

                <Typography
                    sx={{
                        mt: 1,
                        color: "#d56a32",
                        fontSize: "13px",
                        fontWeight: 700,
                        lineHeight: 1.5,
                    }}
                >
                    The order will be created after confirmation.
                </Typography>

                <Box
                    sx={{
                        mt: 3,
                        display: "flex",
                        gap: 1.2,
                    }}
                >
                    <Button
                        variant="outlined"
                        disabled={isLoading}
                        onClick={onClose}
                        sx={{
                            flex: 1,
                            minHeight: "48px",
                            borderRadius: "14px",
                            borderWidth: "2px",
                            borderColor: "#9ecadd",
                            color: "#306781",
                            fontWeight: 800,
                            textTransform: "none",

                            "&:hover": {
                                borderWidth: "2px",
                                borderColor: "#67add0",
                            },
                        }}
                    >
                        Not yet
                    </Button>

                    <Button
                        variant="contained"
                        disabled={isLoading}
                        onClick={onConfirm}
                        sx={{
                            flex: 1,
                            minHeight: "48px",
                            borderRadius: "14px",
                            background:
                                "linear-gradient(145deg, #25ba6c, #1d9e59)",
                            fontWeight: 800,
                            textTransform: "none",
                            boxShadow:
                                "0 10px 22px rgba(37, 186, 108, 0.25)",

                            "&:hover": {
                                background:
                                    "linear-gradient(145deg, #20ad62, #188c4e)",
                            },
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress
                                size={22}
                                sx={{
                                    color: "#ffffff",
                                }}
                            />
                        ) : (
                            "Yes, received"
                        )}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PaymentModal;